/**
 * useOffline – React hook for offline status, sync & conflict detection
 */
import { useState, useEffect, useCallback } from "react";
import { onSyncEvent, processQueue, initOfflineSync } from "../lib/offlineSync";
import { getPendingCount, getPendingPhotoCount } from "../lib/offlineDB";

export function useOffline() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncStatus, setSyncStatus] = useState("idle"); // idle | syncing | done | error
  const [pendingActions, setPendingActions] = useState(0);
  const [pendingPhotos, setPendingPhotos] = useState(0);
  const [conflicts, setConflicts] = useState([]);

  const refreshCounts = useCallback(async () => {
    try {
      const [actions, photos] = await Promise.all([
        getPendingCount(),
        getPendingPhotoCount(),
      ]);
      setPendingActions(actions);
      setPendingPhotos(photos);
    } catch {
      // IndexedDB not available
    }
  }, []);

  const dismissConflict = useCallback((id) => {
    setConflicts(prev => prev.filter(c => c.id !== id));
  }, []);

  const dismissAllConflicts = useCallback(() => {
    setConflicts([]);
  }, []);

  useEffect(() => {
    initOfflineSync();

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    const unsub = onSyncEvent((event, data) => {
      if (event === "sync-start") setSyncStatus("syncing");
      if (event === "sync-complete") {
        setSyncStatus("done");
        refreshCounts();
        setTimeout(() => setSyncStatus("idle"), 3000);
      }
      if (event === "sync-error") setSyncStatus("error");
      if (event === "online") setIsOnline(true);
      if (event === "offline") setIsOnline(false);
      if (event === "sync-item-done" || event === "sync-photo-done") {
        refreshCounts();
      }
      if (event === "sync-conflict" && data) {
        setConflicts(prev => [...prev, {
          id: data.id,
          type: data.type,
          detail: data.detail,
          endpoint: data.endpoint,
          timestamp: new Date().toISOString(),
        }]);
      }
    });

    refreshCounts();
    const interval = setInterval(refreshCounts, 10000);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      unsub();
      clearInterval(interval);
    };
  }, [refreshCounts]);

  const triggerSync = useCallback(() => {
    if (navigator.onLine) processQueue();
  }, []);

  const totalPending = pendingActions + pendingPhotos;

  return {
    isOnline,
    syncStatus,
    pendingActions,
    pendingPhotos,
    totalPending,
    triggerSync,
    refreshCounts,
    conflicts,
    dismissConflict,
    dismissAllConflicts,
  };
}
